/**
 * Hall of Fame aggregations for the sales dashboard F3 (VIB-1104/1105). These
 * are HISTORICAL all-time records — they ignore the period filter. Built on the
 * canonical bf_actual; internal projects excluded; year derived from
 * completed_at in Oslo time.
 *
 *  - Høyeste BF (over år)        → calcBestYearlyBf       (top person×year)
 *  - Beste budsjettering (over år)→ calcBestYearlyBudgetAccuracy (top person×year)
 *  - Største enkeltsalg BF        → calcBiggestSingleSales (top projects)
 *  - Beste kunde over år          → calcBestCustomers      (top customers, Σ BF)
 *
 * Pure — no Supabase/React imports.
 */
import { type DashboardProject, type DashboardOwner } from "./dashboard-bf-calc";
import { osloDateParts } from "./oslo-time";
import { isSalesProject } from "./offer-closed-date";

const numOf = (v: number | null) => Number(v ?? 0);

/** Completed sales projects (all-time, internal excluded, with a completion date). */
function completedSales(projects: DashboardProject[]): DashboardProject[] {
  return projects.filter(
    (p) => isSalesProject(p) && p.project_status === "completed" && p.completed_at != null,
  );
}

function ownersByProject(owners: DashboardOwner[]): Map<string, DashboardOwner[]> {
  const map = new Map<string, DashboardOwner[]>();
  for (const o of owners) {
    const arr = map.get(o.project_id);
    if (arr) arr.push(o);
    else map.set(o.project_id, [o]);
  }
  return map;
}

// ── Høyeste BF (over år) — top person × year ─────────────────────────────

export interface YearlyPersonBf {
  userId: string;
  year: number;
  bfKr: number;
}

export function calcBestYearlyBf(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  limit = 3,
): YearlyPersonBf[] {
  const byProject = ownersByProject(owners);
  const acc = new Map<string, number>(); // key: `${userId}|${year}`
  for (const p of completedSales(projects)) {
    const completedAt = p.completed_at;
    if (!completedAt) continue;
    const { year } = osloDateParts(completedAt);
    const bf = numOf(p.bf_actual);
    for (const o of byProject.get(p.id) ?? []) {
      const key = `${o.user_id}|${year}`;
      acc.set(key, (acc.get(key) ?? 0) + bf * (numOf(o.ownership_pct) / 100));
    }
  }
  return [...acc.entries()]
    .map(([key, bf]) => {
      const [userId, year] = key.split("|");
      return { userId, year: Number(year), bfKr: Math.round(bf) };
    })
    .sort((a, b) => b.bfKr - a.bfKr)
    .slice(0, limit);
}

// ── Beste budsjettering (over år) — top person × year by precision ────────

export interface YearlyPersonAccuracy {
  userId: string;
  year: number;
  deviationPct: number;
  projectCount: number;
}

export function calcBestYearlyBudgetAccuracy(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  limit = 3,
  minProjects = 1,
): YearlyPersonAccuracy[] {
  const byProject = ownersByProject(owners);
  const est = new Map<string, number>();
  const act = new Map<string, number>();
  const projectsByKey = new Map<string, Set<string>>();
  for (const p of completedSales(projects)) {
    const completedAt = p.completed_at;
    if (!completedAt || numOf(p.bf_estimated) <= 0) continue;
    const { year } = osloDateParts(completedAt);
    for (const o of byProject.get(p.id) ?? []) {
      const key = `${o.user_id}|${year}`;
      const w = numOf(o.ownership_pct) / 100;
      est.set(key, (est.get(key) ?? 0) + numOf(p.bf_estimated) * w);
      act.set(key, (act.get(key) ?? 0) + numOf(p.bf_actual) * w);
      const set = projectsByKey.get(key) ?? new Set<string>();
      set.add(p.id);
      projectsByKey.set(key, set);
    }
  }
  return [...est.keys()]
    .map((key) => {
      const [userId, year] = key.split("|");
      const e = est.get(key) ?? 0;
      const a = act.get(key) ?? 0;
      return {
        userId,
        year: Number(year),
        deviationPct: e > 0 ? Math.round(((a - e) / e) * 1000) / 10 : 0,
        projectCount: projectsByKey.get(key)?.size ?? 0,
      };
    })
    .filter((r) => r.projectCount >= minProjects)
    .sort((a, b) => Math.abs(a.deviationPct) - Math.abs(b.deviationPct))
    .slice(0, limit);
}

// ── Største enkeltsalg BF — top single projects ──────────────────────────

export interface SingleSaleRecord {
  projectId: string;
  bfActualKr: number;
  year: number;
}

export function calcBiggestSingleSales(
  projects: DashboardProject[],
  limit = 3,
): SingleSaleRecord[] {
  return completedSales(projects)
    .map((p) => ({
      projectId: p.id,
      bfActualKr: Math.round(numOf(p.bf_actual)),
      year: p.completed_at ? osloDateParts(p.completed_at).year : 0,
    }))
    .sort((a, b) => b.bfActualKr - a.bfActualKr)
    .slice(0, limit);
}

// ── Beste kunde over år — top customers by Σ BF ──────────────────────────

export interface CustomerBfRecord {
  customerId: string;
  bfKr: number;
}

export function calcBestCustomers(
  projects: DashboardProject[],
  limit = 3,
): CustomerBfRecord[] {
  const acc = new Map<string, number>();
  for (const p of completedSales(projects)) {
    if (!p.customer_id) continue;
    acc.set(p.customer_id, (acc.get(p.customer_id) ?? 0) + numOf(p.bf_actual));
  }
  return [...acc.entries()]
    .map(([customerId, bf]) => ({ customerId, bfKr: Math.round(bf) }))
    .sort((a, b) => b.bfKr - a.bfKr)
    .slice(0, limit);
}
